
import { useParams, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { MetalPurityModal } from "@/components/MetalPurityModal";
import { DealerActivationModal } from "@/components/DealerActivationModal";
import { PriceLockRequestModal } from "@/components/PriceLockRequestModal";
import { usePriceStore } from "@/hooks/usePriceStore";
import { MainNav } from "@/components/MainNav";
import {
  DealerDetailHeader,
  DealerDetailLoader,
  DealerDetailNotFound,
  DealerSEO,
  DealerContent
} from "@/components/dealer-detail";
import { ServerDealerContent } from "@/components/dealer-detail/ServerDealerContent";
import { useHydratedDealerDetail } from "@/hooks/dealers/useHydratedDealerDetail";
import { isSSR } from "@/lib/isSSR";
import { useDealerModals } from "@/hooks/dealers/useDealerModals";
import { useDealerUtils } from "@/hooks/dealers/useDealerUtils";
import { useAuthContext } from "@/contexts/auth";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const DealerDetail = () => {
  const { id, city } = useParams<{ id: string, city: string }>();
  const [searchParams] = useSearchParams();
  const { dealer, loading, error, formattedCity, userDistance } = useHydratedDealerDetail();
  const { user, isAuthenticated } = useAuthContext();
  const { goldPrice, silverPrice } = usePriceStore();
  const { 
    showPurityModal, 
    setShowPurityModal,
    showActivationModal, 
    setShowActivationModal,
    showPriceLockModal, 
    setShowPriceLockModal,
    selectedMetal,
    setSelectedMetal,
    handleActivateDealer,
    handlePriceLock,
    handlePuritySelect
  } = useDealerModals();
  
  const { formatCityName, createDealerCanonicalPath, handleGoBack } = useDealerUtils();
  
  // Check for action in URL params and trigger appropriate modal
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'price-lock' && dealer?.activated) {
      // Default to gold
      console.log("Found price-lock action in URL, opening modals");
      setSelectedMetal("ORO");
      setShowPurityModal(true);
    }
  }, [searchParams, dealer, setShowPurityModal, setSelectedMetal]);
  
  // Debug log to track modal states
  useEffect(() => {
    console.log("Modal states:", { 
      showPurityModal, 
      showActivationModal, 
      showPriceLockModal,
      selectedMetal 
    });
  }, [showPurityModal, showActivationModal, showPriceLockModal, selectedMetal]);
  
  // During client-side rendering, show loading or not found states
  if (!isSSR()) {
    if (loading) {
      return (
        <>
          <MainNav />
          <div className="container py-8">
            <DealerDetailHeader handleGoBack={() => handleGoBack(city)} />
            <div className="flex-1 flex justify-center items-center py-12">
              <div className="text-center">
                <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto mb-4" />
                <p className="text-muted-foreground">Caricamento dettagli compro oro...</p>
              </div>
            </div>
          </div>
        </>
      );
    }

    // Only show the error page if there's an actual error AND we're not still loading
    if (error && !loading) {
      return <DealerDetailNotFound handleGoBack={() => handleGoBack(city)} />;
    }
  }

  // Create canonical URL using city from dealer data if available
  const canonicalPath = dealer ? createDealerCanonicalPath(id!, dealer.city, city) : '';
  
  const handlePriceLockWithValidation = (metal: "ORO" | "ARGENTO") => {
    console.log(`Price lock requested for ${metal}`);
    
    if (!isAuthenticated) {
      toast("Per bloccare il prezzo è necessario accedere");
      setTimeout(() => {
        window.location.href = `/auth/login?redirect=${window.location.pathname}?action=price-lock`;
      }, 2000);
      return;
    }
    
    if (!dealer?.activated) {
      toast("Questo compro oro non è ancora attivato");
      return;
    }
    
    // Proceed with price lock flow
    setSelectedMetal(metal);
    setShowPurityModal(true);
  };

  const handleMetalCardClick = (metal: "gold" | "silver") => {
    // Convert from English to Italian metal names
    const metalMap: Record<"gold" | "silver", "ORO" | "ARGENTO"> = {
      gold: "ORO",
      silver: "ARGENTO"
    };
    
    handlePriceLockWithValidation(metalMap[metal]);
  };

  return (
    <>
      {/* Only render SEO component on client-side, server-side SEO is handled by ServerDealerContent */}
      {!isSSR() && dealer && (
        <DealerSEO 
          dealer={dealer}
          formattedCity={formattedCity}
          canonicalPath={canonicalPath}
        />
      )}
      
      <MainNav />
      <div className="container py-8">
        <DealerDetailHeader handleGoBack={() => handleGoBack(city)} />

        {isSSR() ? (
          <ServerDealerContent 
            dealer={dealer || (typeof window !== 'undefined' && (window as any).__SERVER_STORE?.dealerData?.[id])} 
            citySlug={city}
            userDistance={undefined}
          />
        ) : (
          dealer ? (
            <DealerContent 
              dealer={dealer}
              goldPrice={goldPrice}
              silverPrice={silverPrice}
              isAuthenticated={isAuthenticated}
              handleActivateDealer={handleActivateDealer}
              handlePriceLock={handlePriceLockWithValidation}
              userDistance={userDistance}
            />
          ) : loading ? (
            <div className="flex-1 flex justify-center items-center py-12">
              <div className="text-center">
                <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto mb-4" />
                <p className="text-muted-foreground">Caricamento dettagli compro oro...</p>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex justify-center items-center py-12">
              <div className="text-center">
                <p className="text-muted-foreground">Impossibile caricare i dettagli del compro oro</p>
              </div>
            </div>
          )
        )}
      </div>
      
      {dealer && showPurityModal && (
        <MetalPurityModal
          isOpen={showPurityModal}
          onClose={() => setShowPurityModal(false)}
          onConfirm={handlePuritySelect}
          metal={selectedMetal}
        />
      )}
      
      {dealer && showActivationModal && (
        <DealerActivationModal
          dealerId={dealer.id}
          dealerName={dealer.name}
          isOpen={showActivationModal}
          onClose={() => setShowActivationModal(false)}
          onSuccess={() => {
            // Ricarica i dati del dealer per aggiornare lo stato di attivazione
            window.location.reload();
          }}
        />
      )}
      
      {dealer && showPriceLockModal && (
        <PriceLockRequestModal
          isOpen={showPriceLockModal}
          onClose={() => setShowPriceLockModal(false)}
          dealerId={dealer.id}
          dealerName={dealer.name}
          metal={selectedMetal}
        />
      )}
    </>
  );
};

export default DealerDetail;
